import { addDays, format, getDay, parseISO, subDays } from "date-fns";
import { Duty, isDutyAvailable, shortDate } from "./DutyLine";

// Mesmo limite do passo 4 antigo (minDate = hoje − 90 dias, maxDate = hoje).
export const WINDOW_DAYS = 90;
export type Range = { start: string; end: string };
export type WeekGroup = { weekStart: string; weekEnd: string; items: Duty[] };

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const iso = (date: Date) => format(date, "yyyy-MM-dd");
const dayOf = (duty: Duty) => String(duty.date).slice(0, 10);
const shiftMonth = (ym: string, delta: number) => {
  const [y, m] = ym.split("-").map(Number);
  const t = y * 12 + (m - 1) + delta;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};

export const todayIso = (now: Date = new Date()) => iso(now);
export const windowRange = (today: string): Range => ({ start: iso(subDays(parseISO(today), WINDOW_DAYS)), end: today });

export function monthsInWindow(range: Range): string[] {
  const out: string[] = [];
  for (let ym = range.end.slice(0, 7); ym >= range.start.slice(0, 7); ym = shiftMonth(ym, -1)) out.push(ym);
  return out;
}

export function monthBounds(ym: string, range: Range): Range {
  const first = `${ym}-01`;
  const last = iso(subDays(parseISO(`${shiftMonth(ym, 1)}-01`), 1));
  return { start: first < range.start ? range.start : first, end: last > range.end ? range.end : last };
}

// O ME devolve também concluídos antes da data inicial ("Anteriores") e pode devolver plantões depois de hoje
// ("Ainda não realizados"): os dois grupos aparecem, mas não são selecionáveis (como no passo antigo).
export function splitByWindow(duties: Duty[], range: Range): { inside: Duty[]; before: Duty[]; after: Duty[] } {
  return {
    inside: duties.filter((d) => dayOf(d) >= range.start && dayOf(d) <= range.end),
    before: duties.filter((d) => dayOf(d) < range.start),
    after: duties.filter((d) => dayOf(d) > range.end),
  };
}

export function dutiesInMonth(inside: Duty[], ym: string): Duty[] {
  return inside
    .filter((d) => dayOf(d).slice(0, 7) === ym)
    .sort((a, b) => (dayOf(a) === dayOf(b) ? String(a.startHour).localeCompare(String(b.startHour)) : dayOf(a) < dayOf(b) ? 1 : -1));
}

export const countAvailable = (list: Duty[]) => list.filter(isDutyAvailable).length;

export const defaultMonth = (months: string[], inside: Duty[]) => months.find((ym) => countAvailable(dutiesInMonth(inside, ym)) > 0) ?? months[0];

// Semana de domingo a sábado; os rótulos ficam recortados aos limites do mês.
export function groupByWeek(list: Duty[], bounds: Range): WeekGroup[] {
  const groups: WeekGroup[] = [];
  list.forEach((duty) => {
    const date = parseISO(dayOf(duty));
    const ws = iso(subDays(date, getDay(date)));
    const start = ws < bounds.start ? bounds.start : ws;
    let group = groups.find((g) => g.weekStart === start);
    if (!group) {
      const we = iso(addDays(parseISO(ws), 6));
      group = { weekStart: start, weekEnd: we > bounds.end ? bounds.end : we, items: [] };
      groups.push(group);
    }
    group.items.push(duty);
  });
  return groups;
}

export { shortDate };
export const monthName = (ym: string) => MONTHS[Number(ym.slice(5, 7)) - 1];
export const monthShort = (ym: string) => `${MONTHS_SHORT[Number(ym.slice(5, 7)) - 1]}/${ym.slice(0, 4)}`;
export const sumValues = (list: Duty[]) => list.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
